import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { usePopup } from "../context/PopupContext";
import { testimonials, awards } from "../mockData";

export default function About() {
  const { open } = usePopup();

  return (
    <main data-testid="about-page" className="pt-28 pb-16">
      <HeroSection />
      <MissionVisionSection />
      <StatsSection />
      <WhyChooseSection open={open} />
      <AwardsSection />
      <TestimonialsSection />
    </main>
  );
}

/* ─── Hero ─── */
function HeroSection() {
  return (
    <section className="py-20 px-4">
      <div className="max-w-7xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] text-white leading-tight">
            Empowering innovation through <span className="gradient-text">intelligent solutions</span>
          </h1>
          <p className="text-gray-400 text-base sm:text-lg max-w-3xl mx-auto mt-6 leading-relaxed">
            At Digitronix Consultancy, we craft exceptional digital experiences and push the boundaries of innovation. With years of expertise, we collaborate with enterprises and SMEs worldwide, delivering value through software development, business automation, and consulting. Our dedication to excellence and cutting-edge technology positions us as a trusted leader in the digital landscape.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Mission / Vision / Values Tabs ─── */
function MissionVisionSection() {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = ["Mission", "Vision", "Values"];
  const content = [
    {
      title: "Empowering the Future of Business Through Innovation",
      text: "At Digitronix Consultancy, our mission is to empower businesses through innovative, intelligent, and scalable digital solutions. We are committed to delivering cutting-edge systems, automation tools, and transformative technologies that enhance efficiency, fuel growth, and drive sustainable success. With a focus on client-centricity, adaptability, and excellence, we aim to be the trusted partner that helps organizations thrive in a rapidly evolving digital world."
    },
    {
      title: "Our Vision",
      text: "At Digitronix Consultancy, our vision is to revolutionize the digital landscape by delivering cutting-edge solutions and intelligent software that drive business efficiency and innovation. We aim to be a global leader in digital transformation, empowering enterprises with seamless, technology-driven solutions that foster sustainable growth and operational excellence."
    },
    {
      title: "Our Values",
      text: "We are committed to innovation, integrity, and excellence in everything we do. Our customer-centric approach ensures that we build solutions tailored to real business needs. We value collaboration, transparency, and continuous improvement, enabling us to stay ahead in an ever-evolving digital world. At Digitronix Consultancy, we believe in creating long-term partnerships based on trust and delivering impactful results."
    }
  ];

  return (
    <section className="py-20 px-4 bg-[#f8f8fc]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-6">
          Forward-thinking company dedicated to <span className="text-purple-600">innovative digital solutions</span>
        </h2>
        <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
          Digitronix Consultancy is a forward-thinking company dedicated to delivering innovative digital solutions across diverse industries. We empower businesses with cutting-edge technology, driving productivity, efficiency, and sustainable growth.
        </p>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-10">
          {tabs.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                i === activeTab
                  ? "bg-purple-600 text-white"
                  : "bg-white text-gray-600 hover:text-purple-600 border border-gray-200"
              }`}
              data-testid={`mvv-tab-${i}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl p-8 sm:p-12 border border-gray-200 max-w-3xl mx-auto shadow-sm"
        >
          <h3 className="text-gray-900 font-semibold text-xl mb-4">{content[activeTab].title}</h3>
          <p className="text-gray-600 leading-relaxed">{content[activeTab].text}</p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Stats ─── */
function StatsSection() {
  const stats = [
    { value: "200+", label: "Projects Delivered" },
    { value: "150+", label: "Active clients globally" },
    { value: "7+", label: "Years on the market" },
    { value: "4", label: "Offices and delivery centres" },
  ];

  return (
    <section className="py-20 px-4 bg-[#08080f]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-4">
          We build lasting <span className="gradient-text">partnerships</span> with our clients
        </h2>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-14">
          Digitronix Consultancy helps businesses transform by taking their idea and nurturing it into a physical product that offers measurable and noticeable results.
        </p>

        <div className="lg:grid lg:grid-cols-4 lg:gap-8 mobile-slider mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="w-[160px] lg:w-auto text-center">
              <div className="text-3xl sm:text-5xl font-bold text-white">{stat.value}</div>
              <p className="text-gray-400 text-sm mt-2">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Why Choose Us ─── */
function WhyChooseSection({ open }) {
  const reasons = [
    {
      title: "Redefining User Experiences",
      description: "We believe exceptional design builds credibility and fosters engagement. Through human-centered design and in-depth market research, we craft seamless digital experiences that captivate users and drive lasting connections.",
      image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=500&h=350&fit=crop"
    },
    {
      title: "Building Cutting-Edge Solutions",
      description: "From intuitive mobile apps to powerful enterprise software, we transform ideas into reality with full-stack development expertise. Our engineering team ensures robust architecture, seamless functionality, and advanced security.",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=500&h=350&fit=crop"
    },
    {
      title: "Driving Sustainable Growth",
      description: "Harness the power of data-driven strategies, innovative technology, and performance-driven marketing to fuel business expansion. Our advanced analytics help businesses make informed decisions and achieve long-term success.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500&h=350&fit=crop"
    }
  ];

  return (
    <section className="py-20 px-4 bg-[#f8f8fc]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-4">
          Why choose <span className="text-purple-600">Digitronix Consultancy</span>
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-14">
          We drive innovation to propel your business forward. Our team of expert developers, designers, and strategists craft intelligent solutions that enhance efficiency and growth.
        </p>

        <div className="lg:grid lg:grid-cols-3 lg:gap-8 mobile-slider">
          {reasons.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="w-[280px] lg:w-auto bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm card-hover"
            >
              <img src={item.image} alt={item.title} className="w-full h-48 object-cover" loading="lazy" />
              <div className="p-6">
                <h3 className="text-gray-900 font-semibold text-lg mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Awards ─── */
function AwardsSection() {
  return (
    <section className="py-20 px-4 bg-[#08080f]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-4">
          Recognized globally for <span className="gradient-text">digital excellence</span>
        </h2>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-14">
          Our relentless pursuit of innovation and quality has earned us recognition from leading industry organizations worldwide.
        </p>
        <div className="lg:grid lg:grid-cols-6 lg:gap-6 mobile-slider">
          {awards.map((award) => (
            <div key={award.id} className="w-[160px] lg:w-auto bg-white/5 border border-purple-900/20 rounded-2xl p-6 text-center card-hover min-h-[180px] flex flex-col items-center justify-center">
              <div className="w-14 h-14 mx-auto mb-3 bg-purple-600/20 rounded-2xl flex items-center justify-center">
                <span className="text-purple-400 font-bold text-xl">★</span>
              </div>
              <h4 className="text-white font-bold text-sm">{award.name}</h4>
              <p className="text-gray-500 text-xs mt-1">{award.subtitle}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Testimonials ─── */
function TestimonialsSection() {
  return (
    <section className="py-20 px-4 bg-[#f8f8fc] overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-4">
          Trusted by leaders <span className="text-purple-600">across industries</span>
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-14">
          From concept to delivery, we are committed to creating solutions that are intuitive, engaging, and user-friendly.
        </p>
      </div>
      <div className="overflow-hidden">
        <div className="flex animate-marquee">
          {[...testimonials, ...testimonials, ...testimonials].map((t, i) => (
            <div key={i} className="flex-shrink-0 w-[calc(100vw-2rem)] lg:w-[350px] mx-4 bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <p className="text-gray-700 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                <div>
                  <h4 className="text-gray-900 font-semibold text-sm">{t.name}</h4>
                  <p className="text-gray-500 text-xs">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Past Clients ─── */
function ClientsSection() {
  const clients = ["TechVault", "NovaPay", "DataBridge", "GulfTech", "FinServe", "UrbanStyle", "MedCore"];

  return (
    <section className="py-20 px-4 bg-[#08080f] overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-4">
          Our Past <span className="gradient-text">Clients</span>
        </h2>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-14">
          Digitronix Consultancy has been working with clients across the globe, delivering agile solutions and rapid approaches to every challenge.
        </p>
      </div>
      <div className="overflow-hidden">
        <div className="flex animate-marquee">
          {[...clients, ...clients, ...clients, ...clients].map((client, i) => (
            <div key={i} className="flex-shrink-0 mx-10 flex items-center">
              <span className="text-gray-500 text-xl font-bold tracking-wider opacity-60">{client}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
